import { Typography } from '@mui/material';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import type { PlanOverview } from '@weft/api-definition';
import { PLAN_STATUSES } from '@weft/types';
import { isNotNullOrUndefined } from '@weft/utils';
import { useUpdatePlan } from 'apps/app/src/hooks/requests/use-update-plan';
import { runMutation } from 'apps/app/src/utils/vy-mutation';
import React, { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { useDeletePlan } from '../../../../../hooks/requests/use-delete-plan';
import { useMenu } from '../../../../../hooks/use-menu';
import { useVyNavigate } from '../../../../../hooks/use-vy-navigate';
import type { NewPlanAssignment } from '../../plans-page-types/new-plan-assignment-schema';
import type { PlanAssignmentInput } from '../../plans-page-types/plan-assignment-input-schema';

type PlanDotMenuModalProps = {
  children?: React.ReactElement;
  refetchFunction: () => void;
  plan: PlanOverview;
};

export const PlanMenuModal: React.FC<PlanDotMenuModalProps> = ({ children, plan, refetchFunction }) => {
  const navigate = useVyNavigate();
  const { isOpen, handleMenuOpen, handleMenuClose, anchorElement } = useMenu();
  const { setValue } = useFormContext<PlanAssignmentInput, NewPlanAssignment>();

  const updatePlanMutation = useUpdatePlan();

  const deletePlanMutation = useDeletePlan();

  const handleUpdateContract = () => {
    handleMenuClose();
    setValue('contract.planId', plan.id);
  };

  const handleClonePlan = () => {
    navigate(`/plans/create?cloneFromPlanId=${plan.id}`);
  };

  const handleDeletePlan = async () => {
    handleMenuClose();
    await runMutation(
      deletePlanMutation,
      { planId: plan.id },
      {
        pending: 'Deleting plan...',
        success: 'Plan deleted',
        error: 'Failed to delete plan',

      },
      {
        onSuccess:  async () => refetchFunction(),
      });
  };

  const handlePlanStatusChange = useCallback(async () => {
    const status = plan.status === PLAN_STATUSES.Active ? PLAN_STATUSES.Inactive : PLAN_STATUSES.Active;

    handleMenuClose();
    await runMutation(
      updatePlanMutation,
      { planId: plan.id, updatePlanRequest: { status } },
      {
        pending: 'Updating plan...',
        success: 'Plan updated',
        error: 'Failed to update plan',
      },
      {
        onSuccess: async () => refetchFunction(),
      },
    );
  }, [plan]);

  const isActivated = plan.status === PLAN_STATUSES.Active;

  return (
    <>
      {isNotNullOrUndefined(children) && React.cloneElement(children, {
        onClick: handleMenuOpen,
      })}

      <Menu
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        open={isOpen}
        anchorEl={anchorElement}
        onClose={handleMenuClose}
        slotProps={{
          paper: {
            className: 'max-w-[225px] w-full',
          },
        }}
      >
        <MenuItem onClick={handleUpdateContract}>
          <Typography>Assign customer</Typography>
        </MenuItem>

        <MenuItem onClick={handlePlanStatusChange}>
          <Typography>{isActivated ? 'Deactivate' : 'Activate'}</Typography>
        </MenuItem>

        <MenuItem onClick={handleClonePlan}>
          <Typography>Duplicate plan</Typography>
        </MenuItem>

        {!doesPlanHaveContracts(plan) && (
          <MenuItem onClick={handleDeletePlan}>
            <Typography>Delete plan</Typography>
          </MenuItem>
        )}
      </Menu>
    </>
  );
};

function doesPlanHaveContracts(plan: PlanOverview) {
  return plan.customersCount > 0;
}